<template>
  <available-section section="reports">
    <available-section section="reports">
      <main class="report-administrator">
        <header class="report-administrator__header">
          <h1 class="report-administrator__title">Приемы: работа администратора</h1>
          <form class="report-administrator__form">
            <div class="report-administrator__row">
              <date-picker-dialog
                range
                v-bind:value="period"
                v-on:close="onCloseDatePickerDialog"
                v-on:input="onInputPeriod"
              >
                <template v-slot:trigger="{ listenerObject }">
                  <button
                    title="Изменить период"
                    type="button"
                    v-if="hasSelectedFrom"
                    v-on="listenerObject"
                    class="report-administrator__search"
                  >
                    <time v-bind:datetime="$options.convertToInputDate(periodValue[0])">
                      {{ $options.formatDate(periodValue[0]) }}
                    </time>
                    —
                    <time v-bind:datetime="$options.convertToInputDate(periodValue[1])">
                      {{ $options.formatDate(periodValue[1]) }}
                    </time>
                  </button>
                  <button
                    title="Выбрать период"
                    type="button"
                    v-else
                    v-on="listenerObject"
                    class="report-administrator__search"
                  >Выберите период</button>
                </template>
              </date-picker-dialog>
            </div>
            <div class="report-administrator__tags">
              <date-range-presets
                v-model="period"
                v-on:input="onInputPeriod"
              />
            </div>
            <multiple-autocomplete-input
              chips
              class="report-administrator__autocomplete"
              label="ФИО сотрудника"
              multiple
              removal-per-chip
              select-all-text="Все сотрудники"
              set-select-all-option
              small-chips
              v-bind:items="employees"
              v-bind:value="selectedEmployees"
              v-on:input="onInputEmployees" />
            <label class="report-administrator__percent">
              <input
                type="checkbox"
                v-bind:value="inPercent"
                v-on:input="onInputInPercent" />
              <span class="slider" />
              <span class="text">%</span>
            </label>
          </form>
        </header>
        <div class="report-administrator__body">
          <table
            class="report-administrator__table"
            v-if="hasLines">
            <colgroup>
              <col>
              <col class="total" span="4">
              <col class="initial" span="4">
              <col class="repeat" span="4">
            </colgroup>
            <thead class="report-administrator__table__header">
              <tr>
                <th
                  v-for="{ id, title, size } in groups"
                  v-bind:key="'group:' + id"
                  v-bind:colspan="size">
                  {{title}}
                </th>
              <tr>
                <th
                  v-for="{ field, sorts, title } in columns"
                  v-bind:key="'column:' + field">
                  <sort-by-column
                    class="report-administrator__table__sort"
                    v-bind:field="field"
                    v-bind:items="sorts"
                    v-bind:label="title"
                    v-bind:value="sort"
                    v-if="sorts.length"
                    v-on:input="onInputSort" />
                  <template v-else>{{ title }}</template>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="report-administrator__table__row"
                v-for="(line, lineIndex) in lines"
                v-bind:key="'line:' + lineIndex">
                <td
                  class="report-administrator__table__cell"
                  v-for="{ field } in columns"
                  v-bind:data-field="field"
                  v-bind:key="'line:' + lineIndex + ':' + field">{{ line[field] }}</td>
              </tr>
            </tbody>
          </table>
          <i v-else-if="isFetchingReport">Идет построение отчета...</i>
          <strong v-else>Данные по отчету недоступны.</strong>
        </div>
      </main>
    </available-section>
  </available-section>
</template>
<script>
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import { convertToInputDate, formatDate } from '@/utils/timeFormat';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import DateRangePresets from '@/components/DateRangePresets.vue';

/**
 * @description Получить период от месяца назад по сегодняшний день.
 * @returns {[Date, Date]}
 */
const getPeriodFromMonthAgoToToday = () => {
  const to = new Date();
  const currentMonth = to.getMonth();
  const from = new Date();
  from.setMonth(currentMonth - 1);
  return [from, to];
};

/**
 * @description Компонент сортировки по столбцу.
 * @type {Vue}
 */
const SortByColumn = {
  computed: {
    headTitle() {
      const head = this.sortedItems[0];
      return head ? head.title : '';
    },
    isNotSelected() {
      return this.items.some(({ value }) => value === this.value) !== true;
    },
    sortedItems() {
      const items = [
        {
          checked: this.isNotSelected,
          title: 'Без сортировки',
          value: '',
        },
        ...this.items.map(({ title, value }) => ({
          checked: value === this.value,
          title,
          value,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  render(createElement) {
    const name = `report-sort-${this.field}`;
    return createElement('label', { attrs: { title: this.headTitle }, class: !this.isNotSelected ? 'selected' : '' }, [
      createElement('span', this.label).children[0],
      ...this.sortedItems.map(({ checked, title, value }) => createElement('input', {
        attrs: {
          'aria-label': title,
          checked,
          name,
          title,
          type: 'radio',
        },
        domProps: { checked },
        key: `column:${this.field}:sort:${value}`,
        on: { input: () => { this.$emit('input', value); } },
      })),
    ]);
  },
  props: {
    field: {
      required: true,
      type: String,
    },
    items: {
      required: true,
      type: Array,
    },
    label: {
      required: true,
      type: String,
    },
    value: {
      required: true,
      type: String,
    },
  },
};

/**
 * @description Раздел отчета пациентов по администраторам.
 * @type {Vue}
 */
export default {
  components: {
    DateRangePresets,
    AvailableSection,
    DatePickerDialog,
    MultipleAutocompleteInput,
    SortByColumn,
  },
  computed: {
    /**
     * @description Есть строки отчета.
     * @type {boolean}
     */
    hasLines() {
      return this.lines.length > 0;
    },
    /**
     * @description Имеет выбранную дату окончания.
     * @type {boolean}
     */
    hasSelectedTo() {
      return this.period.length === 2;
    },
    /**
     * @description Имеет выбранную дату начала.
     * @type {boolean}
     */
    hasSelectedFrom() {
      return this.period.length > 0;
    },
    /**
     * @description Период.
     * @type {Date[]}
     */
    periodValue() {
      if (this.period.length === 1) {
        const [from] = this.period;
        return [from, from];
      }
      if (this.period.length === 2) {
        return this.period;
      }
      return [];
    },
  },
  convertToInputDate,
  created() {
    this.fetchColumnsAndLines();
    this.fetchFilters();
  },
  data() {
    const selectedPresetPeriod = 'month';
    return {
      columns: [],
      employees: [],
      groups: [],
      inPercent: false,
      isFetchingReport: false,
      lines: [],
      period: getPeriodFromMonthAgoToToday(),
      selectedEmployees: [],
      selectedPresetPeriod,
      sort: 'by-name-asc',
    };
  },
  formatDate,
  methods: {
    /**
     * @description Получить колонки и строки отчета.
     * @returns {void}
     */
    fetchColumnsAndLines() {
      if (this.hasSelectedFrom !== true) {
        return;
      }
      this.isFetchingReport = true;
      this
        .$reportGateway
        .getPatientReportByAdministrators(
          this.selectedEmployees,
          convertToInputDate(this.period[0]),
          convertToInputDate(this.period[1]),
          this.inPercent,
          this.sort,
        )
        .then((result) => {
          result.forEach(({ columns, groups, lines }) => {
            this.columns = columns;
            this.groups = groups;
            this.lines = lines;
          });
        })
        .finally(() => { this.isFetchingReport = false; });
    },
    /**
     * @description Получить фильтры.
     * @returns {void}
     */
    fetchFilters() {
      this
        .$reportGateway
        .getPatientReportFiltersByAdministrators()
        .then((result) => {
          result.forEach(({ employees }) => { this.employees = employees; });
        });
    },
    /**
     * @description Обработать закрытие диалога выборы даты.
     * @returns {void}
     */
    onCloseDatePickerDialog() {
      if (this.period.length === 1) {
        const [left] = this.period;
        this.onInputPeriod([left, left]);
      }
    },
    onInputEmployees(value) {
      this.selectedEmployees = value;
      this.fetchColumnsAndLines();
    },
    /**
     * @description Обработать выбор показ в процентах.
     * @param {Event} event Значение.
     * @returns {void}
     */
    onInputInPercent(event) {
      this.inPercent = event.target.checked;
      this.fetchColumnsAndLines();
    },
    /**
     * @description Обработать выбор периода.
     * @param {[Date, Date]} value Значение.
     * @returns {void}
     */
    onInputPeriod(value) {
      this.selectedPresetPeriod = '';
      this.period = value.sort(
        (first, second) => Number(first.getTime() > second.getTime()),
      );
      if (this.period.length === 2) {
        this.fetchColumnsAndLines();
      }
    },
    onInputSort(value) {
      this.sort = value;
      this.fetchColumnsAndLines();
    },
  },
  name: 'ClinicPanelReportPatientsAdministrators',
};
</script>
<style lang="scss">
.menuable__content__active {
  min-width: 250px !important;
}
.report-administrator {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__form {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    gap: 23px;
    margin: 30px 0 0 0;
  }
  &__row {
    display: flex;
    flex-direction: row;
  }
  &__search {
    border: 1px solid #C2CFE0;
    border-radius: 4px;
    background: #FFFFFF;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    height: 40px;
    min-width: 250px;
    width: 100%;
    padding: 0 0 0 10px;
    display: flex;
    align-items: center;
    position: relative;
    &:after {
      content: '';
      background-color: #EAEFF3;
      border-radius: 0 4px 4px 0;
      width: 38px;
      height: 38px;
      position: absolute;
      right: 0;
      overflow: hidden;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: center;
    }
  }
  &__tags {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 4px;
    overflow: hidden;
  }
  &__tag {
    &>input {
      position: absolute;
      z-index: -1;
      opacity: 0;
      &:checked+span {
        background: #0489E0;
        color: #FFFFFF;
      }
    }
    &>span {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      background: #EAEFF3;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
      color: #192A3E;
      padding: 0 13px;
      cursor: pointer;
      &:hover {
        background: #0489E0;
        color: #FFFFFF;
      }
    }
  }
  &__autocomplete {
    min-width: 300px !important;
    max-width: fit-content;
    &.v-input .v-label {
      height: 32px;
      line-height: 32px;
    }
    &.v-text-field .v-label {
      top: 7px;
    }
    label.v-label.v-label--active.theme--light {
      top: 0;
    }
    &.v-text-field .v-input__append-inner, .v-text-field .v-input__prepend-inner {
      margin-top: 12px;
    }
  }
  &__checkbox {
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 10px;
    font-weight: 400;
    font-size: 18px;
  }
  &__percent {
    position: relative;
    width: 38px;
    height: 26px;
    input {
      position: absolute;
      opacity: 0;
      width: 38px;
      height: 26px;
      &:checked + .slider {
        background-color: rgba(4, 137, 224, 0.1);
        border: 1px solid #0489E0;
      }
      &:checked + .slider::before {
        background-color: #0489E0;
        -webkit-transform: translateX(12px);
        -ms-transform: translateX(12px);
        transform: translateX(12px);
      }
    }
    .slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 34px;
      border: 1px solid #c2cfe0;
      transition: .2s;
      &::before {
        position: absolute;
        top: 2px;
        left: 2px;
        content: "";
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background-color: #90a0b7;
        transition: .2s;
      }
    }
    .text {
      position: absolute;
      left: 50px;
      cursor: pointer;
      font-weight: 400;
      font-size: 18px;
    }
  }
  &__body {
    padding: 37px 20px 48px 23px;
    overflow: auto;
  }
  &__table {
    font-size: 14px;
    line-height: 14px;
    border-collapse: collapse;
    width: 100%;
    overflow: auto;
    colgroup {
      col:nth-child(2n) {
        background: #F4F7F9;
      }
    }
    &__header {
      font-weight: 700;
      border-bottom: 2px solid #0489E0;
      th {
        padding: 10px 2px;
        label {
          input {
            display: none;
          }
        }
      }
      th {
        &:first-child {
          text-align: left;
          padding: 10px 8px;
        }
      }
    }
    &__row {
      border-bottom: 1px solid #EAEFF3;
      td {
        &:first-child {
          text-align: left;
          padding: 15px 8px;
        }
      }
    }
    &__cell {
      padding: 15px 2px;
      text-align: center;
    }
    &__sort {
      cursor: pointer;
      &:after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      &.selected {
        color: var(--action-color);
      }
    }
  }
}
@media screen and (max-width: 1500px) {
  .report-administrator {
    &__table {
      font-size: 13px;
    }
  }
}
@media screen and (max-width: 1240px) {
  .report-administrator {
    &__table {
      width: 1200px;
    }
  }
}
@media screen and (max-width: 480px) {
  .menuable__content__active {
    min-width: initial;
  }
  .report-administrator {
    &__header {
      padding: 40px 15px;
    }
    &__body {
      padding: 22px 15px 50px;
    }
    &__autocomplete {
      min-width: initial;
    }
  }
}
</style>
